import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
  Tabs,
  Tab,
  Typography,
  Drawer,
  IconButton,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
} from "@mui/material";
import {
  themes,
  drawerStyle,
  parentBoxStyle,
  doubleArrowStyle,
  cardBorder,
  tableRowSize,
  editIconStyle,
  childBoxStyleSpaceBetween,
} from "services/constants";

import {
  ArrowRight,
  DoubleArrow,
  Edit,
  InfoOutlined,
} from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CustomTextBox } from "components/CustomComponents";
import { CustomButtonWithBorder } from "components/CustomComponents/CustomButton";
import sessionHandling from "services/utils/notificationUtils";
import { showToast } from "services/utils/Status";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import { tooltipStyleProps } from "services/constants/Styles";
import { EditMultipleCheckInOut } from "./EditMultipleCheckInOut";
import { Loader } from "components/Loader";
import { UserDetailsRow } from "components/CustomComponents/UserDetailsRow";
import { useForm } from "react-hook-form";
import { customAPI } from "services/utils/CustomAPI";
import { CustomTimePicker } from "components/CustomComponents/CustomTimePicker";
import { onlyNumberRegex, rgxWithDot } from "services/constants/patterns";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { PunchEntries } from "./PunchEntries";
import { checkEditorPermission } from "services/utils/HelperFunction";

const theme = createTheme({
  palette: {
    primary: {
      main: "#00D4AA",
    },
    secondary: {
      main: "#6c757d",
    },
  },
  typography: {
    fontFamily:
      '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
});

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 1 }}>{children}</Box>}
    </div>
  );
}

const InfoRow = ({ label, value, tooltip }) => (
  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography sx={{ fontWeight: 600, color: "#757575" }}>
        {label}
      </Typography>

      {tooltip && (
        <Tooltip title={tooltip} arrow slotProps={tooltipStyleProps}>
          <InfoOutlined
            sx={{ fontSize: 18, color: "#9e9e9e", cursor: "pointer" }}
          />
        </Tooltip>
      )}
    </Box>

    <Typography sx={{ fontWeight: 600 }}>{value}</Typography>
  </Box>
);

export const EditCheckInOut = ({
  setOpenSidePeek,
  openSidePeek,
  shiftDetails,
  setEditState,
  departmentId,
}) => {
  const { domain, token, userScreenAccess } = useSelector(
    (state) => state.tokenReducer
  );
  const [tabValue, setTabValue] = useState(0);
  const [doAnyAct, setDoAnyAct] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [attendanceDetails, setAttendanceDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recallApi, setRecallApi] = useState(true);
  const [editDetail, setEditDetail] = useState(false);
  const [isEditRecord, setIsEditRecord] = useState(false);
  const [url, setUrl] = useState(null);
  const [sessionDetails, setSessionDetails] = useState("");
  const [isUpdate, setIsUpdate] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [getUpdatedData, setGetUpdatedData] = useState(false);
  const {
    setValue,
    handleSubmit,
    register,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      pay_cycle: "",
      payout_policy: "",
      reason: "",
      button_loader: false,
    },
  });

  useEffect(() => {
    if (attendanceDetails?.date && attendanceDetails?.user_id) {
      setUrl(
        `${domain}Team_Zone/Approve/Over_time/ot-post-approve/?selected_user_id=${
          attendanceDetails?.user_id
        }&ot_date=${attendanceDetails?.date.split("-").reverse().join("-")}`
      );
    }
  }, [attendanceDetails, domain]);

  const { result: otSessionData, setLoad: reCallApi } = useFetch(url);

  useEffect(() => {
    if (getUpdatedData) {
      reCallApi(true);
      setGetUpdatedData(false);
    }
  }, [getUpdatedData, reCallApi, recallApi]);

  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Attendance",
    "Status"
  );

  const preShiftData =
    attendanceDetails?.ot_allowed &&
    (attendanceDetails?.ot_consideration === "Both" ||
      attendanceDetails?.ot_consideration === "PreOT");

  const postShiftData =
    attendanceDetails?.ot_allowed &&
    (attendanceDetails?.ot_consideration === "Both" ||
      attendanceDetails?.ot_consideration === "PostOT");

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Mui.Tab" || event.key === "Shift")
    ) {
      return;
    }
    setOpenSidePeek(open);
    if (open === false) {
      if (doAnyAct) {
        setDoAnyAct(false);
      }
    }
  };

  const handleToggle = (index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  useEffect(() => {
    const fetchLeaveGroupDatas = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${domain}attendance_manual_entry/?user_id=${
            shiftDetails?.user_id
          }&date=${shiftDetails?.date?.split("-").reverse().join("-")}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          }
        );
        const res = await response.json();
        if (response.ok) {
          setAttendanceDetails(res);
          setRecallApi(false);
          setLoading(false);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error(res.error);
        }
      } catch (error) {
        showToast("error", error.message);
        if (
          error.message ===
          "Editing entries before 25-Mar-2025 is not supported."
        )
          setRecallApi(false);
        setLoading(false);
      }
    };
    if (recallApi) {
      fetchLeaveGroupDatas();
    }
  }, [domain, recallApi, shiftDetails?.date, shiftDetails?.user_id, token]);

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    const response = await customAPI(
      `${domain}Team_Zone/Approve/Over_time/ot-post-approve/`,
      "PUT",
      {
        approval_id: sessionDetails?.approval_id,
        payout_policy: data?.payout_policy,
        status: "Approved",
        ...(sessionDetails?.ot_calculation_based_on === "Hours" && {
          approved_qty: dayjs(data?.durationHrs).format("HH:mm"),
        }),
        ...(sessionDetails?.ot_calculation_based_on === "Shift" && {
          approved_qty: data?.no_of_shifts,
        }),
        ...(sessionDetails?.ot_calculation_based_on === "Amount" && {
          approved_qty: data?.amount,
        }),
      }
    );
    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      setIsUpdate(false);
      setOpenSidePeek(false);
      setEditState(true);
      reCallApi(true);
      setValue("button_loader", false);
    } else {
      showToast("error", response?.message);
      setValue("button_loader", false);
    }
  };

  const handleReject = async () => {
    setValue("button_loader", true);
    const response = await customAPI(
      `${domain}Team_Zone/Approve/Over_time/ot-post-approve/`,
      "PUT",
      {
        approval_id: sessionDetails?.approval_id,
        status: "Rejected",
        rejected_reason: watch("reason"),
      }
    );
    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      setIsUpdate(false);
      setOpenSidePeek(false);
      setEditState(true);
      setValue("button_loader", false);
      reCallApi(true);
    } else {
      showToast("error", response?.message);
      setValue("button_loader", false);
    }
  };

  useEffect(() => {
    if (sessionDetails && isUpdate) {
      if (sessionDetails?.ot_calculation_based_on === "Hours") {
        dayjs(sessionDetails?.credited_qty, "HH:mm", true).isValid() &&
          setValue(
            "durationHrs",
            sessionDetails?.status === "Approved"
              ? dayjs(sessionDetails?.approved_qty, "HH:mm", true)
              : dayjs(sessionDetails?.credited_qty, "HH:mm", true)
          );
      } else if (sessionDetails?.ot_calculation_based_on === "Shift") {
        setValue(
          "no_of_shifts",
          sessionDetails?.status === "Approved"
            ? sessionDetails?.approved_qty
            : sessionDetails?.credited_qty
        );
      } else {
        setValue(
          "amount",
          sessionDetails?.status === "Approved"
            ? parseInt(sessionDetails?.approved_qty)
            : parseInt(sessionDetails?.credited_qty)
        );
      }
      setValue("payout_policy", sessionDetails?.payout_policy);
      setIsUpdate(false);
    }
  }, [isUpdate, sessionDetails, setValue, watch]);

  const updateNumberOfShifs = (event) => {
    if (event.target.value && rgxWithDot.test(event.target.value)) {
      setValue("no_of_shifts", event.target.value);
    } else {
      setValue("no_of_shifts", "");
    }
  };

  const updateAmount = (event) => {
    if (event.target.value && onlyNumberRegex.test(event.target.value)) {
      setValue("amount", event.target.value);
    } else {
      setValue("amount", "");
    }
  };

  const handleCancelClose = () => {
    setShowConfirmationDialog(false);
    reset();
  };

  const handleReason = (event) => {
    const reason = event.target.value;
    if (reason) {
      setValue("reason", event.target.value);
    } else {
      setValue("reason", "");
    }
  };

  const headerWrapperSx = {
    bgcolor: themes?.primary,
    borderBottom: "1px solid #c8cbd3",
    borderRadius: "5px 5px 0 0",
  };

  const headerGridSx = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    p: 1,
  };

  const headerTextSx = {
    fontWeight: 600,
    textAlign: "center",
  };

  return (
    <React.Fragment>
      <ThemeProvider theme={theme}>
        <Drawer
          anchor="right"
          open={openSidePeek}
          onClose={toggleDrawer("right", false)}
          sx={drawerStyle}
        >
          <Box sx={parentBoxStyle}>
            <Paper
              elevation={0}
              sx={{
                borderBottom: `1px solid ${themes?.miliedLightGray}`,
                borderRadius: 0,
              }}
            >
              <Box sx={childBoxStyleSpaceBetween}>
                <IconButton
                  onClick={() => setOpenSidePeek(false)}
                  sx={doubleArrowStyle}
                >
                  <DoubleArrow />
                </IconButton>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip
                    title="Edit Record"
                    arrow
                    slotProps={tooltipStyleProps}
                  >
                    <IconButton
                      onClick={() => {
                        if (check_access) {
                          setEditDetail(true);
                          setIsEditRecord(true);
                          setTabValue(0);
                          setExpandedIndex(null);
                        } else {
                          showToast(
                            "error",
                            `Access denied: You are not authorized to edit attendance details`
                          );
                        }
                      }}
                      sx={editIconStyle}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Box sx={{ ml: 2.2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Attendance Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View employee shift details, hours worked, and overtime for
                  payroll.
                </Typography>
              </Box>
            </Paper>

            <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
              <Box>
                <UserDetailsRow
                  userDetails={attendanceDetails}
                  isEditRecord={isEditRecord}
                />
                <Box>
                  <Tabs
                    selectionFollowsFocus
                    textColor={themes.primaryIndicator}
                    TabIndicatorProps={{
                      style: {
                        backgroundColor: themes.primaryIndicator,
                      },
                    }}
                    value={tabValue}
                    onChange={handleTabChange}
                    centered
                    variant="fullWidth"
                  >
                    <Tab
                      aria-label="phone"
                      sx={{
                        "&.Mui-selected": {
                          color: "#09bd71",
                        },
                        "&:not(.Mui-selected)": {
                          backgroundColor: themes.shadowBackGround,
                        },
                      }}
                      label={
                        <Typography
                          sx={{
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        >
                          Shift Map
                        </Typography>
                      }
                      iconPosition="end"
                    />
                    <Tab
                      aria-label="phone"
                      sx={{
                        "&.Mui-selected": {
                          color: "#09bd71",
                        },
                        "&:not(.Mui-selected)": {
                          backgroundColor: themes.shadowBackGround,
                        },
                      }}
                      label={
                        <Typography
                          sx={{
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        >
                          Punch Entries
                        </Typography>
                      }
                      iconPosition="end"
                    />
                  </Tabs>

                  {loading ? (
                    <Loader />
                  ) : (
                    <TabPanel value={tabValue} index={0}>
                      <ConfirmationDialog
                        open={showConfirmationDialog}
                        onClose={handleCancelClose}
                        onConfirm={() => {
                          handleReject();
                        }}
                        title={"Reason"}
                        confirmText={"Reject"}
                        cancelText="Cancel"
                        showChildren={true}
                      >
                        <Box sx={{ mb: 1 }}>
                          <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
                            Reason (Optional)
                          </Typography>
                          {showConfirmationDialog && (
                            <CustomTextBox
                              type="multiline"
                              rules={{
                                ...register("reason", {
                                  required: false,
                                }),
                              }}
                              selectedValue={watch("reason")}
                              updateDataFunction={handleReason}
                              placeHolder={"Enter a reason for deletion"}
                            />
                          )}
                        </Box>
                      </ConfirmationDialog>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Timing
                        </Typography>
                        {preShiftData &&
                          attendanceDetails?.pre_ot_data?.length > 0 && (
                            <Box sx={{ spaceY: 1 }}>
                              <Box sx={{ mb: 2, border: "1px solid #c8cbd3" }}>
                                {attendanceDetails?.pre_ot_data?.length > 0 && (
                                  <Box sx={headerWrapperSx}>
                                    <Box sx={headerGridSx}>
                                      <Typography />
                                      <Typography sx={headerTextSx}>
                                        Start
                                      </Typography>
                                      <Typography sx={headerTextSx}>
                                        End
                                      </Typography>
                                      <Typography sx={headerTextSx}>
                                        Duration
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                                <Box sx={{ borderBottom: "1px solid #c8cbd3" }}>
                                  {attendanceDetails?.pre_ot_data?.map(
                                    (row, index) => (
                                      <Box
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns:
                                            "1fr 1fr 1fr 1fr",
                                          p: 1,
                                          borderBottom: "1px solid #c8cbd3",
                                        }}
                                      >
                                        <Typography
                                          variant="body1"
                                          sx={{ fontWeight: 600 }}
                                        >
                                          OT Session 1
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            textAlign: "center",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {row?.ot_start_time ?? "00:00"}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            textAlign: "center",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {row?.ot_end_time ?? "00:00"}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            textAlign: "center",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {row?.ot_duration ?? "00:00"}
                                        </Typography>
                                      </Box>
                                    )
                                  )}
                                  {/* Break */}
                                  {attendanceDetails?.pre_ot_data?.[0]?.break_data?.map(
                                    (item, index) => (
                                      <Box
                                        key={index}
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns:
                                            "1fr 1fr 1fr 1fr",
                                          p: 1,
                                          bgcolor: "rgba(234, 171, 53, 0.1)",
                                        }}
                                      >
                                        <Typography
                                          variant="body1"
                                          sx={{ fontWeight: 600 }}
                                        >
                                          Break
                                        </Typography>

                                        <Typography
                                          variant="body2"
                                          sx={{ textAlign: "center" }}
                                        >
                                          {item.break_start ?? "00:00"}
                                        </Typography>

                                        <Typography
                                          variant="body2"
                                          sx={{ textAlign: "center" }}
                                        >
                                          {item.break_end ?? "00:00"}
                                        </Typography>

                                        <Typography
                                          variant="body2"
                                          sx={{ textAlign: "center" }}
                                        >
                                          {item.break_duration ?? "00:00"}
                                        </Typography>
                                      </Box>
                                    )
                                  )}
                                </Box>

                                <Box
                                  sx={{
                                    bgcolor: themes?.primary,
                                    p: 1,
                                    borderRadius: "0 0 5px 5px",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Typography variant="body1">
                                        Duration
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {attendanceDetails?.pre_ot_data[0]
                                          ?.ot_duration ?? "00:00"}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Typography variant="body1">
                                        Worked
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {attendanceDetails?.pre_ot_data[0]
                                          ?.ot_worked_duration ?? "00:00"}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{ display: "flex", gap: 1, mr: 1.5 }}
                                    >
                                      <Typography variant="body1">
                                        Break
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {attendanceDetails?.pre_ot_data[0]
                                          ?.break_duration ?? "00:00"}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          )}

                        <Box sx={{ mb: 2, border: "1px solid #c8cbd3" }}>
                          <Box sx={headerWrapperSx}>
                            <Box sx={headerGridSx}>
                              <Typography />
                              <Typography sx={headerTextSx}>Start</Typography>
                              <Typography sx={headerTextSx}>End</Typography>
                              <Typography sx={headerTextSx}>
                                Duration
                              </Typography>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              borderBottom: "1px solid #c8cbd3",
                              borderRadius: "5px 5px 0 0",
                            }}
                          >
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                p: 1,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600 }}
                              >
                                Shift
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  textAlign: "center",
                                  fontWeight: 600,
                                }}
                              >
                                {attendanceDetails?.shift_in_time ?? "-"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  textAlign: "center",
                                  fontWeight: 600,
                                }}
                              >
                                {attendanceDetails?.shift_out_time ?? "-"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  textAlign: "center",
                                  fontWeight: 600,
                                }}
                              >
                                {attendanceDetails?.shift_working_hours ??
                                  "00:00"}
                              </Typography>
                            </Box>
                          </Box>

                          {attendanceDetails?.shift_data?.break_data?.map(
                            (breakItem, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                  p: 1,
                                  bgcolor: "rgba(234, 171, 53, 0.1)",
                                  borderBottom: "1px solid #c8cbd3",
                                }}
                              >
                                {index === 0 && (
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 600,
                                      gridRow: "span 3",
                                    }}
                                  >
                                    Break
                                  </Typography>
                                )}
                                {index !== 0 && <Box></Box>}
                                <Typography
                                  variant="body2"
                                  sx={{ textAlign: "center" }}
                                >
                                  {breakItem.break_start}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ textAlign: "center" }}
                                >
                                  {breakItem.break_end}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ textAlign: "center" }}
                                >
                                  {breakItem.break_duration}
                                </Typography>
                              </Box>
                            )
                          )}

                          <Box
                            sx={{
                              bgcolor: themes?.primary,
                              p: 1,
                              borderRadius: "0 0 5px 5px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Typography variant="body1">
                                  Duration
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {attendanceDetails?.shift_working_hours ??
                                    "00:00"}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Typography variant="body1">Worked</Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {attendanceDetails.shift_working_duration ??
                                    "00:00"}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", gap: 1, mr: 1.5 }}>
                                <Typography variant="body1">Break</Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {attendanceDetails?.shift_data
                                    ?.break_duration ?? "00:00"}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                        {postShiftData &&
                          attendanceDetails?.post_ot_data?.length > 0 && (
                            <Box sx={{ mb: 2, border: "1px solid #c8cbd3" }}>
                              {attendanceDetails?.post_ot_data?.length > 0 && (
                                <Box sx={headerWrapperSx}>
                                  <Box sx={headerGridSx}>
                                    <Typography />
                                    <Typography sx={headerTextSx}>
                                      Start
                                    </Typography>
                                    <Typography sx={headerTextSx}>
                                      End
                                    </Typography>
                                    <Typography sx={headerTextSx}>
                                      Duration
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                              <Box
                                sx={{
                                  borderBottom: "1px solid #c8cbd3",
                                  borderRadius: "5px 5px 0 0",
                                }}
                              >
                                {attendanceDetails?.post_ot_data?.map(
                                  (row, index) => (
                                    <Box
                                      sx={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                        p: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        OT Session 2
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          textAlign: "center",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {row?.ot_start_time}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          textAlign: "center",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {row?.ot_end_time}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          textAlign: "center",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {row?.ot_duration}
                                      </Typography>
                                    </Box>
                                  )
                                )}
                              </Box>

                              {attendanceDetails?.post_ot_data?.[0]?.break_data?.map(
                                (item, index) => (
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                      p: 1,
                                      bgcolor: "rgba(234, 171, 53, 0.1)",
                                      borderBottom: "1px solid #c8cbd3",
                                    }}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      Break
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ textAlign: "center" }}
                                    >
                                      {item?.break_start}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ textAlign: "center" }}
                                    >
                                      {item?.break_end}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ textAlign: "center" }}
                                    >
                                      {item?.break_duration}
                                    </Typography>
                                  </Box>
                                )
                              )}

                              <Box
                                sx={{
                                  bgcolor: "rgba(60, 115, 231, 0.05)",
                                  p: 1,
                                  borderRadius: "0 0 5px 5px",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    <Typography variant="body1">
                                      Duration
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {attendanceDetails?.post_ot_data[0]
                                        ?.ot_duration ?? "00:00"}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    <Typography variant="body1">
                                      Worked
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {attendanceDetails?.post_ot_data[0]
                                        ?.ot_worked_duration ?? "00:00"}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{ display: "flex", gap: 1, mr: 1.5 }}
                                  >
                                    <Typography variant="body1">
                                      Break
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {attendanceDetails?.post_ot_data[0]
                                        ?.break_duration ?? "00:00"}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          )}

                        <Divider />

                        <Box sx={{ ...cardBorder, p: 1, px: 2, py: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Typography variant="body1">
                                Shift Worked
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600 }}
                              >
                                {attendanceDetails?.shift_working_hours ??
                                  "00:00"}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Typography variant="body1">
                                Total Break
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600 }}
                              >
                                {attendanceDetails?.total_break_hours ??
                                  "00:00"}
                              </Typography>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mt: 1.5,
                            }}
                          >
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Typography variant="body1">
                                Total worked
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600 }}
                              >
                                {attendanceDetails?.total_working_hours ??
                                  "00:00"}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Typography variant="body1">Total OT</Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600 }}
                              >
                                {attendanceDetails?.total_ot_worked_duration ??
                                  "00:00"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />
                        {otSessionData?.length > 0 && (
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Approve Overtime
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Approve employee overtime entries to include
                              eligible hours in the payroll processing.
                            </Typography>
                            <TableContainer
                              sx={{
                                border: "1px solid #c8cbd3",
                                borderRadius: "6px",
                                mt: 0.5,
                              }}
                            >
                              <Table>
                                <TableHead>
                                  <TableRow
                                    sx={{
                                      ...tableRowSize,
                                      bgcolor: themes?.primary,
                                    }}
                                  >
                                    <TableCell width={40}></TableCell>
                                    <TableCell>Session</TableCell>
                                    <TableCell>Start Time</TableCell>
                                    <TableCell>Worked Hrs</TableCell>
                                    <TableCell>Approved Qty</TableCell>
                                    <TableCell>Status</TableCell>
                                  </TableRow>
                                </TableHead>

                                <TableBody>
                                  {otSessionData?.map((session, index) => (
                                    <React.Fragment key={index}>
                                      <TableRow
                                        onClick={() => {
                                          setIsUpdate(true);
                                          handleToggle(index);
                                          setSessionDetails(session);
                                          reset();
                                        }}
                                        sx={tableRowSize}
                                      >
                                        <TableCell width={40}>
                                          <ArrowRight
                                            sx={{
                                              transition: "0.3s",
                                              transform:
                                                expandedIndex === index
                                                  ? "rotate(90deg)"
                                                  : "rotate(0deg)",
                                              cusror: "pointer",
                                            }}
                                          />
                                        </TableCell>

                                        <TableCell>
                                          {session?.session_name ?? "-"}
                                        </TableCell>
                                        <TableCell>
                                          {session?.start_time ?? "-"}
                                        </TableCell>
                                        <TableCell>
                                          {session?.worked_hours ?? "-"}
                                        </TableCell>
                                        <TableCell>
                                          {session?.approved_qty ?? "-"}
                                        </TableCell>
                                        <TableCell>
                                          {session?.status ?? "-"}
                                        </TableCell>
                                      </TableRow>

                                      {expandedIndex === index && (
                                        <TableRow>
                                          <TableCell
                                            colSpan={5}
                                            sx={{ p: 2.5 }}
                                          >
                                            <Box sx={{ p: 0.2 }}>
                                              {session?.ot_work_authorization !==
                                                "Manual" && (
                                                <>
                                                  <Typography
                                                    sx={{
                                                      fontWeight: 600,
                                                      mb: 1,
                                                      letterSpacing: "-0.3px",
                                                    }}
                                                  >
                                                    Pre Authorization
                                                  </Typography>

                                                  <Card sx={cardBorder}>
                                                    <CardContent sx={{ p: 2 }}>
                                                      <Box
                                                        sx={{
                                                          display: "grid",
                                                          gridTemplateColumns:
                                                            "repeat(2, 1fr)",
                                                          gap: "24px 80px",
                                                        }}
                                                      >
                                                        <InfoRow
                                                          label="Start Time"
                                                          value={
                                                            session?.start_time ??
                                                            "-"
                                                          }
                                                        />
                                                        <InfoRow
                                                          label="Preapproved Qty"
                                                          value={
                                                            session?.preapproved_qty ??
                                                            "-"
                                                          }
                                                        />
                                                        <InfoRow
                                                          label="Duration"
                                                          value={
                                                            session?.pre_ot_duration ??
                                                            "-"
                                                          }
                                                        />
                                                        <InfoRow
                                                          label="Total Worked"
                                                          value={
                                                            session?.total_working_hours ??
                                                            "-"
                                                          }
                                                        />
                                                      </Box>

                                                      <Box
                                                        sx={{
                                                          display: "flex",
                                                          gap: 1,
                                                          mt: 2,
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="body1"
                                                          sx={{
                                                            color: "#4c5c6b",
                                                          }}
                                                        >
                                                          Pre approved by
                                                        </Typography>
                                                        <Typography
                                                          sx={{
                                                            fontWeight: 600,
                                                          }}
                                                        >
                                                          {session?.preapproved_by ??
                                                            "-"}
                                                        </Typography>
                                                        <Typography
                                                          variant="body1"
                                                          sx={{
                                                            color: "#4c5c6b",
                                                          }}
                                                        >
                                                          On
                                                        </Typography>
                                                        <Typography
                                                          sx={{
                                                            fontWeight: 600,
                                                          }}
                                                        >
                                                          {session?.preapproved_on ??
                                                            "-"}
                                                        </Typography>
                                                      </Box>
                                                    </CardContent>
                                                  </Card>
                                                </>
                                              )}

                                              <Typography
                                                sx={{
                                                  fontWeight: 600,
                                                  mb: 1,
                                                  mt: 1,
                                                  letterSpacing: "-0.3px",
                                                }}
                                              >
                                                Approval
                                              </Typography>
                                              <form
                                                onSubmit={handleSubmit(
                                                  onSubmit
                                                )}
                                              >
                                                <Card
                                                  sx={{
                                                    border: "1px solid #c8cbd3",
                                                  }}
                                                >
                                                  <CardContent sx={{ p: 3 }}>
                                                    <Box
                                                      sx={{
                                                        display: "grid",
                                                        gridTemplateColumns:
                                                          "repeat(2, 1fr)",
                                                        gap: "24px 80px",
                                                      }}
                                                    >
                                                      <InfoRow
                                                        label="Consideration"
                                                        value={
                                                          session?.consideration ??
                                                          "-"
                                                        }
                                                      />
                                                      <InfoRow
                                                        label="OT Cal Based on"
                                                        value={
                                                          session?.ot_calculation_based_on ??
                                                          "-"
                                                        }
                                                      />
                                                      <InfoRow
                                                        label="Worked Hrs"
                                                        value={
                                                          session?.worked_hrs ??
                                                          "-"
                                                        }
                                                      />
                                                      <InfoRow
                                                        label="Ot Credited QTY"
                                                        value={
                                                          session?.credited_qty ??
                                                          "-"
                                                        }
                                                        tooltip="The credited quantity is calculated based on grade hours. The payment factor will be applied during payroll calculation."
                                                      />
                                                    </Box>

                                                    <Box sx={{ mt: 3 }}>
                                                      <Box
                                                        sx={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: 2,
                                                        }}
                                                      >
                                                        <Typography
                                                          sx={{
                                                            fontWeight: 600,
                                                          }}
                                                        >
                                                          Approved Qty
                                                        </Typography>
                                                        <Box
                                                          sx={{ width: 405 }}
                                                        >
                                                          {session?.ot_calculation_based_on ===
                                                          "Hours" ? (
                                                            <CustomTimePicker
                                                              name="durationHrs"
                                                              control={control}
                                                              errors={errors}
                                                              validateFn={(
                                                                value
                                                              ) => {
                                                                if (
                                                                  !value ||
                                                                  !dayjs(
                                                                    value
                                                                  ).isValid()
                                                                ) {
                                                                  return "Duration is required";
                                                                }
                                                                return true;
                                                              }}
                                                            />
                                                          ) : session?.ot_calculation_based_on ===
                                                            "Shift" ? (
                                                            <CustomTextBox
                                                              selectedValue={watch(
                                                                "no_of_shifts"
                                                              )}
                                                              rules={{
                                                                ...register(
                                                                  "no_of_shifts",
                                                                  {
                                                                    required:
                                                                      "No of shift is required",
                                                                  }
                                                                ),
                                                              }}
                                                              updateDataFunction={
                                                                updateNumberOfShifs
                                                              }
                                                              placeHolder="Enter no of shifts"
                                                              errorMessage={
                                                                errors
                                                                  ?.no_of_shifts
                                                                  ?.message
                                                                  ? errors
                                                                      ?.no_of_shifts
                                                                      ?.message
                                                                  : ""
                                                              }
                                                              textBoxError={
                                                                errors.no_of_shifts
                                                                  ? true
                                                                  : false
                                                              }
                                                            />
                                                          ) : (
                                                            <CustomTextBox
                                                              selectedValue={watch(
                                                                "amount"
                                                              )}
                                                              rules={{
                                                                ...register(
                                                                  "amount",
                                                                  {
                                                                    required:
                                                                      "Amount is required",
                                                                  }
                                                                ),
                                                              }}
                                                              updateDataFunction={
                                                                updateAmount
                                                              }
                                                              placeHolder="Enter amount"
                                                              errorMessage={
                                                                errors?.amount
                                                                  ?.message
                                                                  ? errors
                                                                      ?.amount
                                                                      ?.message
                                                                  : ""
                                                              }
                                                              textBoxError={
                                                                errors.amount
                                                                  ? true
                                                                  : false
                                                              }
                                                            />
                                                          )}
                                                        </Box>
                                                      </Box>
                                                    </Box>
                                                    <Box
                                                      sx={{
                                                        mt: 3,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 2,
                                                      }}
                                                    >
                                                      <Typography
                                                        sx={{
                                                          fontWeight: 600,
                                                          mb: 1,
                                                        }}
                                                      >
                                                        Add payout to
                                                      </Typography>
                                                      <RadioGroup
                                                        row
                                                        value={watch(
                                                          "payout_policy"
                                                        )}
                                                        onChange={(e) =>
                                                          setValue(
                                                            "payout_policy",
                                                            e.target.value
                                                          )
                                                        }
                                                        sx={{ gap: "20px" }}
                                                      >
                                                        <FormControlLabel
                                                          value="Next Payrun"
                                                          control={<Radio />}
                                                          label="Next Payrun"
                                                        />
                                                        <FormControlLabel
                                                          value="Backlog"
                                                          control={<Radio />}
                                                          label="Backlog"
                                                        />
                                                      </RadioGroup>
                                                    </Box>
                                                    {session?.status !==
                                                      "Approved" &&
                                                      session?.status !==
                                                        "Rejected" && (
                                                        <Box
                                                          sx={{
                                                            mt: 3,
                                                            display: "flex",
                                                            justifyContent:
                                                              "end",
                                                            gap: 1,
                                                          }}
                                                        >
                                                          <CustomButtonWithBorder
                                                            actionName="Reject"
                                                            handleColor={true}
                                                            typeName="button"
                                                            actionFuntion={() =>
                                                              setShowConfirmationDialog(
                                                                true
                                                              )
                                                            }
                                                            disableAction={watch(
                                                              "button_loader"
                                                            )}
                                                          />

                                                          <CustomButtonWithBorder
                                                            actionName="Approve"
                                                            typeName="submit"
                                                            disableAction={watch(
                                                              "button_loader"
                                                            )}
                                                          />
                                                        </Box>
                                                      )}
                                                  </CardContent>
                                                </Card>
                                              </form>
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </Box>
                    </TabPanel>
                  )}
                </Box>

                <Box sx={{ mt: 1 }}>
                  <TabPanel value={tabValue} index={1}>
                    <PunchEntries
                      attendanceDetails={attendanceDetails}
                      setTabValue={setTabValue}
                      setEditState={setEditState}
                      setRecallApi={setRecallApi}
                      shiftDetails={shiftDetails}
                    />
                  </TabPanel>
                </Box>
              </Box>
            </Box>
          </Box>
        </Drawer>
      </ThemeProvider>
      {editDetail ? (
        <EditMultipleCheckInOut
          editDetail={editDetail}
          setEditDetail={setEditDetail}
          shiftDetails={shiftDetails}
          setEditState={setEditState}
          departmentId={departmentId}
          setRecallApi={setRecallApi}
          isEditRecord={isEditRecord}
          setIsEditRecord={setIsEditRecord}
          reCallApi={reCallApi}
          setGetUpdatedData={setGetUpdatedData}
        />
      ) : null}
    </React.Fragment>
  );
};
